import { useMutation } from '@apollo/client'
import { Delete } from '@mui/icons-material'
import { IconButton, Rating, Stack, Typography } from '@mui/material'
import { useIdentity } from 'client/hooks'
import { DeleteRosterCandidateClientNote } from 'shared/graphql/clientNotes'
import useToast from './Toast/useToast'

export interface ClientNoteProps {
    noteId: string
    rating?: number
    updatedAt?: Date
    createdAt: Date
    mostRecentAuthor: {
        id: string
        preferredName: string
    }
    note: string
    onDelete?: () => void
    allowDeleteOverride?: boolean
}

export const clientNotesDivName = 'roster-client-notes'

const ClientNote: React.FC<ClientNoteProps> = ({
    note,
    rating,
    updatedAt,
    mostRecentAuthor,
    noteId,
    createdAt,
    onDelete,
    allowDeleteOverride = false,
}) => {
    const toast = useToast()
    const identity = useIdentity()
    const [deleteNote] = useMutation(DeleteRosterCandidateClientNote)

    return (
        <Stack direction="column" spacing={2} className={clientNotesDivName}>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                {note}
            </Typography>
            <Stack
                direction={'row'}
                spacing={2}
                alignItems={'center'}
                justifyContent={'space-between'}
            >
                <Typography variant="detail">
                    {getClientNoteAuthor(
                        createdAt,
                        mostRecentAuthor.preferredName,
                        updatedAt
                    )}
                </Typography>
                <Stack direction="row" alignItems={'center'} spacing={0}>
                    <Rating precision={0.5} disabled value={rating}></Rating>
                    {(allowDeleteOverride ||
                        identity.client?.user?.id === mostRecentAuthor.id) && (
                        <IconButton
                            onClick={() =>
                                deleteNote({
                                    variables: {
                                        rosterCandidateClientNoteId: noteId,
                                    },
                                    onCompleted: (res) => {
                                        const { success, message } =
                                            res.deleteRosterCandidateClientNote
                                        toast({
                                            title: success
                                                ? 'Success'
                                                : 'Error',
                                            message: success
                                                ? 'Note deleted successfully'
                                                : message ??
                                                  'Unable to delete note, please try again',
                                            severity: success
                                                ? 'success'
                                                : 'error',
                                        })
                                        if (success) onDelete?.()
                                    },
                                    onError: (error) => {
                                        console.error(
                                            'Error deleting note:',
                                            error
                                        )
                                        toast({
                                            title: 'Error',
                                            message:
                                                'Unable to delete note, please try again',
                                            severity: 'error',
                                        })
                                    },
                                })
                            }
                        >
                            <Delete />
                        </IconButton>
                    )}
                </Stack>
            </Stack>
        </Stack>
    )
}

export function getClientNoteAuthor(
    createdAt: Date,
    preferredName: string,
    updatedAt?: Date
): string {
    return `${new Date(updatedAt ?? createdAt).toLocaleDateString(
        'en-US'
    )} ${new Date(updatedAt ?? createdAt).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
    })} • ${preferredName}`
}

export default ClientNote
